import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText
} from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import ShareIcon from '@mui/icons-material/Share';
import LocalHospital from '@mui/icons-material/LocalHospital';
import Home from '@mui/icons-material/Home';
import { API_BASE_URL } from '../../lib/constants';
import theme from '../../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, hasDraft, loadDraft } from '../../utils/draftUtils';
import { useNotification } from '../../contexts/NotificationContext';
const PostOperativeForm2 = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { uhid_no } = useParams(); // Extract uhid_no directly from params
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'handOverBy':
        if (!value) error = "Hand Over by is required";
        break;
      case 'takenOverBy':
        if (!value) error = "Taken Over by is required";
        break;
      case 'relatives_name':
        if (!value || !value.trim()) error = "Relatives name is required";
        break;
      case 'shiftedToWard':
        if (!value) error = "Shifted to Ward time is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };
  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "", // Use the param value or empty string
    reports: {
      LabReports: { recovery: "", ward: "" },
      XRay: { recovery: "", ward: "" },
      "Echo/PFT/TMT": { recovery: "", ward: "" },
      ECG: { recovery: "", ward: "" },
      USG: { recovery: "", ward: "" },
      MRI: { recovery: "", ward: "" },
      CaseSheetOperativeNotes: { recovery: "", ward: "" },
      Others: { recovery: "", ward: "" },
    },
    relatives_name: "",
    histopathologySample: "",
    relatives_signature: "",
    shiftedToWard: "",
    handOverBy: "",
    takenOverBy: "",
    handover_signature: "",
  });

  // Auto-load draft if present on mount
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [uhid_no]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`postOpForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`postOpForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setFormData(saved);
    }
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`postOpForm_${uhid_no}`);
    sessionStorage.removeItem(`formStatus_postOp_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_postOp_${uhid_no}`, JSON.stringify({
        formName: 'Post Operative Handover',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/postOperativeForm2/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const handleInputChange = (field, type, value) => {
    setFormData((prev) => ({
      ...prev,
      reports: {
        ...prev.reports,
        [field]: { ...prev.reports[field], [type]: value },
      },
    }));
  };

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };
  const handleDialogClose = () => {
    setSignatureDialogOpen(false);
  };
  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(',')[1],
    }));
    setSignatureDialogOpen(false);
  };
  const handOverOptions = [
    "Dr. John Smith",
    "Dr. Emily Johnson",
    "Dr. Michael Brown",
    "Dr. Sarah Davis",
    "Dr. William Wilson",
    "Dr. Linda Martinez"
  ];
  const takenOverOptions = [
    "Dr. Robert Lee",
    "Dr. Patricia Taylor",
    "Dr. James Anderson",
    "Dr. Barbara Thomas",
    "Dr. David Jackson",
    "Dr. Susan White"
  ];
  const validate = () => {
    const requiredFields = [
      { key: 'handOverBy',     label: 'Hand Over By'      },
      { key: 'takenOverBy',    label: 'Taken Over By'     },
      { key: 'relatives_name', label: "Relative's Name"   },
      { key: 'shiftedToWard',  label: 'Shifted to Ward Time' },
    ];
    const newErrors = {};
    requiredFields.forEach(({ key, label }) => {
      const val = formData[key];
      if (!val || (typeof val === 'string' && !val.trim()))
        newErrors[key] = `${label} is required`;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
      showNotification('error', requiredFields.filter(f => newErrors[f.key]).map(f => f.label));
    }
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    // Format date for submission
    const formattedOrderDate = formData.shiftedToWard
      ? new Date(formData.shiftedToWard)
      : new Date();

    const formattedOrderDatetime = formattedOrderDate
      .toISOString()
      .slice(0, 19)
      .replace("T", " ");

    const payload = {
      uhid_no: formData.uhid_no,
      ip_no: formData.ip_no,
      lab_reports_recovery_ot: formData.reports.LabReports.recovery,
      lab_reports_ward: formData.reports.LabReports.ward,
      xray_recovery_ot: formData.reports.XRay.recovery,
      xray_ward: formData.reports.XRay.ward,
      echo_pft_tmt_recovery_ot: formData.reports["Echo/PFT/TMT"].recovery,
      echo_pft_tmt_ward: formData.reports["Echo/PFT/TMT"].ward,
      ecg_recovery_ot: formData.reports.ECG.recovery,
      ecg_ward: formData.reports.ECG.ward,
      usg_recovery_ot: formData.reports.USG.recovery,
      usg_ward: formData.reports.USG.ward,
      mri_recovery_ot: formData.reports.MRI.recovery,
      mri_ward: formData.reports.MRI.ward,
      case_sheet_notes_recovery_ot: formData.reports.CaseSheetOperativeNotes.recovery,
      case_sheet_notes_ward: formData.reports.CaseSheetOperativeNotes.ward,
      others_recovery_ot: formData.reports.Others.recovery,
      others_ward: formData.reports.Others.ward,
      histopathology_sample_shown: formData.histopathologySample === "Yes" ? 1 : 0,
      relatives_name: formData.relatives_name,
      relatives_signature: formData.relatives_signature,
      shifted_to_ward_time: formattedOrderDatetime,
      handover_by: formData.handOverBy,
      takenover_by: formData.takenOverBy,
      handover_signature: formData.handover_signature,
    };

    try {
      if (success) {
        await axios.put(`${API_BASE_URL}/update_post_op_handover/${uhid_no}`, payload);
        showNotification('success', 'Post-operative Handover Form updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/submit_post_op_handover`, payload);
        showNotification('success', 'Post-operative Handover Form submitted successfully!');
      }
      // Clear form status after successful submission
      clearSavedData();

      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'PostOperativePhase', status: 'success', step: 3 },
      });
    } catch (error) {
      console.error("There was an error submitting the form!", error);
      showNotification('error', 'Error in submitting Post-operative Handover form!');
    }
  };
  const handleBack = () => {
    navigate(`/mainPage/${formData.uhid_no}`, {
      state: { from: 'PostOperativePhase', status: 'success', step: 3 },
    }); // Navigate to the previous page
  };
  // Fetch next UHID & IP_No on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [ipRes] = await Promise.all([

          axios.get(`${API_BASE_URL}/postop_handover_next_ip`)
        ]);
        setFormData(prev => ({
          ...prev,

          ip_no: ipRes.data.next_ip_no
        }));
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchInitialData();
  }, []);
  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);

      console.log(response.data);
      setFormData(prev => ({
        ...prev,

        name: response.data.name

      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };
  // Fetch data when component mounts
  useEffect(() => {
    if (!uhid_no) return;

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/get_post_op_handover/${uhid_no}`
        );

        console.log("API Response Data:", response.data);

        if (response.data && Object.keys(response.data).length > 0) {
          // Map backend data to frontend structure
          const backendData = response.data;

          // Create reports object from individual fields in backend response
          const reportsData = {
            LabReports: {
              recovery: backendData.lab_reports_recovery_ot || "",
              ward: backendData.lab_reports_ward || ""
            },
            XRay: {
              recovery: backendData.xray_recovery_ot || "",
              ward: backendData.xray_ward || ""
            },
            "Echo/PFT/TMT": {
              recovery: backendData.echo_pft_tmt_recovery_ot || "",
              ward: backendData.echo_pft_tmt_ward || ""
            },
            ECG: {
              recovery: backendData.ecg_recovery_ot || "",
              ward: backendData.ecg_ward || ""
            },
            USG: {
              recovery: backendData.usg_recovery_ot || "",
              ward: backendData.usg_ward || ""
            },
            MRI: {
              recovery: backendData.mri_recovery_ot || "",
              ward: backendData.mri_ward || ""
            },
            CaseSheetOperativeNotes: {
              recovery: backendData.case_sheet_notes_recovery_ot || "",
              ward: backendData.case_sheet_notes_ward || ""
            },
            Others: {
              recovery: backendData.others_recovery_ot || "",
              ward: backendData.others_ward || ""
            }
          };

          // Format the date for shifted_to_ward_time if it exists
          const shiftedDateTime = backendData.shifted_to_ward_time
            ? new Date(backendData.shifted_to_ward_time).toISOString().slice(0, 16)
            : "";

          // Set form data with proper structure
          setFormData({
            uhid_no: backendData.uhid_no || uhid_no,
            ip_no: backendData.ip_no || "",
            reports: reportsData,
            relatives_name: backendData.relatives_name || "",
            histopathologySample: backendData.histopathology_sample_shown ? "Yes" : "No",
            relatives_signature: backendData.relatives_signature || "",
            shiftedToWard: shiftedDateTime,
            handOverBy: backendData.handover_by || "",
            takenOverBy: backendData.takenover_by || "",
            handover_signature: backendData.handover_signature || "",
          });

          setSuccess(true);
        } else {
          // If no data found, initialize with uhid_no from URL
          setFormData(prevData => ({
            ...prevData,
            uhid_no: uhid_no
          }));
          setSuccess(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // If error occurs, still set the uhid_no from URL
        setFormData(prevData => ({
          ...prevData,
          uhid_no: uhid_no
        }));
        setSuccess(false);
      }
    };

    fetchData();
  }, [uhid_no]);
  const handleNameChange = (e) => {
    const regex = /^[a-zA-Z\s.-]*$/; // allow letters, spaces, dot, hyphen
    const value = e.target.value;
    if (value === '' || regex.test(value)) {
      setFormData({ ...formData, [e.target.name]: value });
      if (errors[e.target.name]) {
        setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
      }
    }
  };
  const goToVideoRecorder = (formName, uhid_no) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const handleShareClick = async () => {
    try {
      // Call API to generate a share token for the given UHID
      const res = await axios.post(`${API_BASE_URL}/generate_for_post_op_handover_share_token/${uhid_no}`);

      const token = res.data.token;
      const link = `${API_BASE_URL}/postOperative/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative analgesia chart:\n\n${link}`;

      // Copy the shareable link to clipboard
      await navigator.clipboard.writeText(link);

      // Show success notification to user
      setSubmitStatus({
        open: true,
        message: "Secure link copied to clipboard!",
        severity: "info",
      });

      // Open WhatsApp chat with pre-filled message (replace phone number if needed)
      const phoneNumber = '+916379605575'; // no spaces or special chars for wa.me
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);

      // Show error notification to user
      setSubmitStatus({
        open: true,
        message: "Failed to generate shareable link",
        severity: "error",
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              POST - OPERATIVE
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>

          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            {/* Left-aligned Back Button */}
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("Post Operative Handover", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>
          <br></br>
          <h5>Handed over To Ward Nurse</h5>
          <br></br>
          <Grid item xs={6} sm={3}>
            <TextField
              label="UHID No"
              type="number"
              value={formData.uhid_no}
              onChange={(e) =>
                setFormData((prevState) => ({ ...prevState, uhid_no: e.target.value }))
              }
              required
              fullWidth
              InputProps={{ readOnly: true, }}
            />
          </Grid>
          <br />
          <Grid item xs={6} sm={2}>
            <TextField
              label="IP No"
              InputProps={{ readOnly: true, }}
              value={formData.ip_no}
              onChange={(e) =>
                setFormData((prevState) => ({ ...prevState, ip_no: e.target.value }))
              }
              type="number"
              required
              fullWidth

            />
          </Grid>
          <br />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Criteria</TableCell>
                  <TableCell>Recovery / OT</TableCell>
                  <TableCell>Ward</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(formData.reports).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell>{key.replace(/([a-z])([A-Z])/g, "$1 $2")}</TableCell>
                    <TableCell>
                      <TextField
                        value={value.recovery || ""}
                        type="number"
                        onChange={(e) => handleInputChange(key, "recovery", e.target.value)}
                        fullWidth

                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={value.ward || ""}
                        onChange={(e) => handleInputChange(key, "ward", e.target.value)}
                        fullWidth

                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Signature Section */}
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ marginTop: 2 }}>
                Signature of Scrub Nurse & Circulating Nurse
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    key: "relatives_signature",
                    label: "Relative's Signature"
                  },
                  {
                    key: "handover_signature",
                    label: "Handover Signature"
                  }
                ].map((person, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Typography variant="body1">{person.label}</Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ marginTop: 2 }}
                      onClick={() => handleSignatureOpen(person.key)}
                    >
                      Sign Here
                    </Button>
                    {formData[person.key] && (
                      <img
                        src={`data:image/png;base64,${formData[person.key]}`}
                        alt="Signature"
                        style={{
                          marginTop: 10,
                          border: '1px solid black',
                          width: '100%',
                          height: '150px'
                        }}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
            <DialogTitle>Provide Your Signature</DialogTitle>
            <DialogContent>
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{
                  width: 400,
                  height: 200,
                  className: 'sigCanvas'
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} >close</Button>
              <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
              <Button onClick={handleSignatureSave} color="primary">Done</Button>
            </DialogActions>
          </Dialog>

          {/* Histopathology Sample Section */}
          <Box mt={2}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                Histopathology sample shown to patient relatives
              </FormLabel>
              <RadioGroup
                row
                value={formData.histopathologySample}
                onChange={(e) =>
                  setFormData({ ...formData, histopathologySample: e.target.value })
                }
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Additional Fields */}
          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Shifted to Ward Recovery at"
                variant="outlined"
                fullWidth
                type="DateTime-local" inputProps={{
                  min: new Date().toISOString().slice(0, 16), // Sets the minimum date and time to the current date and time
                }}
                name="shiftedToWard"
                value={formData.shiftedToWard}
                onChange={(e) => {
                  setFormData({ ...formData, shiftedToWard: e.target.value });
                  if (errors.shiftedToWard) setErrors(prev => ({ ...prev, shiftedToWard: "" }));
                }}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                required
                error={!!errors.shiftedToWard}
                helperText={errors.shiftedToWard}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Relatives name"
                variant="outlined"
                fullWidth
                value={formData.relatives_name}
                name="relatives_name"
                onChange={handleNameChange}
                onBlur={handleBlur}
                required
                error={!!errors.relatives_name}
                helperText={errors.relatives_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required sx={{ marginTop: 1 }} error={!!errors.handOverBy}>
                <InputLabel id="handover-label">Hand Over by</InputLabel>
                <Select
                  labelId="handover-label"
                  id="handover-select"
                  name="handOverBy"
                  value={formData.handOverBy || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, handOverBy: e.target.value });
                    if (errors.handOverBy) setErrors(prev => ({ ...prev, handOverBy: "" }));
                  }}
                  onBlur={handleBlur}
                  label="Hand Over by"
                >
                  {handOverOptions.map((doctor, index) => (
                    <MenuItem key={index} value={doctor}>
                      {doctor}
                    </MenuItem>
                  ))}
                </Select>
                {errors.handOverBy && <FormHelperText>{errors.handOverBy}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" required sx={{ marginTop: 1 }} error={!!errors.takenOverBy}>
                <InputLabel id="takenover-label">Taken Over by</InputLabel>
                <Select
                  labelId="takenover-label"
                  id="takenover-select"
                  name="takenOverBy"
                  value={formData.takenOverBy || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, takenOverBy: e.target.value });
                    if (errors.takenOverBy) setErrors(prev => ({ ...prev, takenOverBy: "" }));
                  }}
                  onBlur={handleBlur}
                  label="Taken Over by"
                >
                  {takenOverOptions.map((doctor, index) => (
                    <MenuItem key={index} value={doctor}>
                      {doctor}
                    </MenuItem>
                  ))}
                </Select>
                {errors.takenOverBy && <FormHelperText>{errors.takenOverBy}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.POST_OPERATIVE_ORDERS}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                variant="outlined"
                size="large"
                isUpdate={success}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ marginTop: 3 }}
                fullWidth
                onClick={handleSubmit}
              >
                {success ? 'Update' : 'Submit'}
              </Button>
            </Box>
          </Grid>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PostOperativeForm2;
